import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { storeWrapper } from '../storeWrapper'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '../useMattersStore/useMattersStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import AppRouter from '@repo/app-assets/AppRouter'

const initialState = {
  path: '',
  queryParams: {},
  routeId: '',
  urlSegments: [],
  breadcrumbs: [],
  isHome: false,
  postLoadAction: null,
  postLoadActionShouldWaitForIframeToLoad: false,
  postLoadPage: '',
  postLoadSelector: '',
  iframeSrc: `login.aspx`, // what the tell the iframe to load
  iframeURL: '' // the actual location of the iframe. can be different if we don't have a React route that matches
}

export const useUrlStore = storeWrapper('useUrlStore', (set, get) => ({
  ...initialState,
  handleUrlUpdate: (routeParams) => {
    // when the url updates, break it into segments (/) and use each to populate the breadcrumb menu

    // get the url segments
    const { databaseSuffix } = useGlobalSettingsStore.getState()
    const { hash } = window.location
    const hashSegment = hash.replace(databaseSuffix, '').replace('#', '').replace(/^\//, '').split('?')[0]
    const path = decodeURIComponent(hashSegment)
    const urlSegments = path.split('/')

    // derive query params from hash (window.location.search does not work when the query is appended to a hash)
    const search = hash.split('?')[1]
    let pathSearch = decodeURIComponent(search)
    let queryParams = {}
    if (pathSearch?.length)
      queryParams = pathSearch.split('&').reduce((acc, item) => {
        const [key, value] = item.split('=')
        acc[key] = value
        return acc
      }, {})

    // path is the actual URL in the address bar, which might include params, e.g. matters/matter-workspace/C0001-2
    // this section swaps the params for the param label, e.g. matters/matter-workspace/:clientMatterNo
    let routeId = path
    Object.keys(routeParams).forEach((param) => {
      routeId = routeId.replace(routeParams[param], `:${param}`)
    })

    // update the breadcrumb menu based on the URL
    const menuStructureWithRoutes = useMenuItemsStore.getState().menuStructureWithRoutes
    let breadcrumbs = getBreadcrumbLevels(routeId.split('/'), menuStructureWithRoutes)
    breadcrumbs = breadcrumbs.filter((item) => !!item && !!item.label)

    // finally update the store
    set({ breadcrumbs, path, urlSegments, routeId, isHome: !routeId, queryParams, hash })
  },
  replaceQueryParams: ({ param, value }) => {
    const { queryParams } = get()
    if (!queryParams || !param || !queryParams[param]) return
    if (queryParams[param] === value) return

    const stringToReplace = `${param}=${queryParams[param]}`
    const newHash = window.location.hash.replace(stringToReplace, `${param}=${value}`)
    return newHash.replace('#', '')
  },
  setIframeSrc: (iframeSrc) => set({ iframeSrc }),
  setIframeURL: (iframeURL) => set({ iframeURL }),
  setPostLoadAction: ({ postLoadAction, postLoadActionShouldWaitForIframeToLoad, postLoadPage, postLoadSelector }) =>
    set({ postLoadAction, postLoadActionShouldWaitForIframeToLoad, postLoadPage, postLoadSelector }),
  resetPostLoadAction: () =>
    set({
      postLoadAction: null,
      postLoadActionShouldWaitForIframeToLoad: false,
      postLoadPage: '',
      postLoadSelector: ''
    }),
  getLastUrlSegment: () => {
    // get the last url segment of the current path
    const urlSegments = get()
      .routeId.split('/')
      .filter((segment) => !segment.includes(':'))
      .filter((segment) => !!segment)
    const lastUrlSegment = urlSegments[urlSegments.length - 1]

    const toSentenceCase = (str) => {
      if (!str) return ''
      const removedHyphens = str.split('-').join(' ')
      const firstChar = removedHyphens.charAt(0).toUpperCase()
      return firstChar + removedHyphens.slice(1)
    }

    return {
      lastUrlSegmentLabel: toSentenceCase(lastUrlSegment).replace('Add edit', 'Add/edit'),
      lastUrlSegment
    }
  },
  urlEncodeClientMatterNo: ({ clientNo, matterNo, clientMatterNo }) => {
    if (clientMatterNo) {
      const extracted = extractClientMatterNumbers({ clientMatterNo })
      if (extracted) {
        clientNo = extracted.clientNo
        matterNo = extracted.matterNo
      }
    }

    if (clientNo && (matterNo || matterNo === 0 || matterNo === '0')) {
      try {
        const encodedClientMatterNo = encodeURIComponent(`${clientNo}-${matterNo}`)
        return encodedClientMatterNo
      } catch (error) {
        console.error('Error encoding clientMatterNo:', error)
      }
    }

    return clientMatterNo || `${clientNo}-${matterNo}`
  },
  urlDecodeClientMatterNo: ({ clientMatterNoParam }) => {
    if (!clientMatterNoParam) return null

    // handle trailing slash
    if (clientMatterNoParam.endsWith('/')) {
      clientMatterNoParam = clientMatterNoParam.slice(0, -1)
    }

    if (!clientMatterNoParam) return null

    if (!clientMatterNoParam.includes('-')) {
      console.error('lastUrlSegment is not a clientMatterNo:', clientMatterNoParam)
      return { clientNo: clientMatterNoParam, matterNo: null, clientMatterNo: clientMatterNoParam }
    }

    let decodedClientMatterNo

    try {
      decodedClientMatterNo = decodeURIComponent(clientMatterNoParam)
    } catch (error) {
      console.error('Error decoding clientMatterNo:', error)
    }

    const clientMatterNo = decodedClientMatterNo || clientMatterNoParam
    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })
    return { clientNo, matterNo, clientMatterNo }
  },
  getQueryParams: ({ route, path, clientNo, matterNo }) => {
    let queryParams = route.split('?').length > 1 ? route.split('?')[1] : ''

    if (path.includes('case-management/clients/client-detail/edit-client') && !queryParams.includes('key1')) {
      queryParams += `key1=${clientNo}`
    } else if (path.includes('case-management/matters/matter-detail/edit-matter') && !queryParams.includes('key1')) {
      queryParams += `key1=${clientNo}&key2=${matterNo}`
    } else if (path.includes('case-management/matters/matter-page') && !queryParams.includes('viewextensions')) {
      queryParams += `viewextensions=true`
    } else if (path.includes('case-management/matters/document-production') && !queryParams.includes('clientNo')) {
      queryParams += `clientNo=${clientNo}&matterNo=${matterNo}`
    }

    if (queryParams && !queryParams.startsWith('?')) {
      queryParams = `?${queryParams}`
    }

    return queryParams
  },
  navigateWithClientMatterNo: ({ route, appendClientMatterNo = false, clientMatterNo }) => {
    let clientNo
    let matterNo
    const { currentClientMatterNo, matters } = useMattersStore.getState()

    if (!route) {
      route = useUserPreferencesStore.getState().defaultMatterPage || '/case-management/matters/matter-workspace'
      appendClientMatterNo = true
    }

    // clientMatterNo is not specified, so use the current client/matter from the store
    if (!clientMatterNo && currentClientMatterNo) {
      clientMatterNo = currentClientMatterNo
    }

    // make sure we have the clientNo and matterNo if we have clientMatterNo
    if (clientMatterNo) {
      const matter = matters[clientMatterNo]
      if (matter?.isUserRestricted) {
        alert('You are not authorised to access this matter')
        return
      }

      const extracted = extractClientMatterNumbers({ clientMatterNo })
      if (extracted) {
        clientNo = extracted.clientNo
        matterNo = extracted.matterNo
      }
    }

    let path = route.split('?')[0]
    if (!path.startsWith('/') && !path.startsWith('http')) {
      path = `/${path}`
    }

    let queryParams = get().getQueryParams({ path, route, clientNo, matterNo })

    // only change the matter if the clientMatterNo is different to the current one - in that case, the matter will be changed when they go to the URL
    const shouldChangeMatter = clientMatterNo && clientMatterNo !== currentClientMatterNo
    if (shouldChangeMatter) {
      // we send the user to the change-matter page. This is really important - underneath that page is an Osprey 1 page that will update the current matter in the Osprey 1 context (since not all Osprey 1 pages fo that correctly. it will load that page and then redirect onto our target route)
      path = `/change-matter`
      queryParams += `${queryParams ? '&' : '?'}redirectTo=${route}&appendClientMatterNo=${appendClientMatterNo}`
    }

    const encodedClientMatterNo = clientMatterNo ? get().urlEncodeClientMatterNo({ clientMatterNo }) : ''

    const routeParams =
      encodedClientMatterNo && (appendClientMatterNo || path === '/change-matter') ? `/${encodedClientMatterNo}` : ''

    const { isOutlookAddin, ospreySite } = useGlobalSettingsStore.getState()
    const domain = isOutlookAddin && ospreySite ? `${ospreySite}/app/#` : ''
    const protocol = domain && !domain.startsWith('https://') ? 'https://' : ''

    // construct the full route
    const routeToNavigateTo = `${protocol}${domain}${path}${routeParams}${queryParams}`

    console.table({
      routeToNavigateTo,
      clientMatterNo,
      clientNo,
      matterNo,
      appendClientMatterNo,
      encodedClientMatterNo,
      queryParams
    })

    // if we're not returning a URL, navigate to the route
    const router = AppRouter()
    if (isOutlookAddin) {
      window.open(routeToNavigateTo, '_blank')
    } else if (router && routeToNavigateTo) {
      router.navigate(routeToNavigateTo)
    }
  },
  reset: () => {
    set({ ...initialState })
  }
}))

const getBreadcrumbLevels = (urlSegments, menuItems) => {
  const breadcrumbLevels = []
  let menuToSearch = menuItems

  urlSegments.forEach((urlSegment, index) => {
    // filter out missing values, or those with routeParams
    if (!urlSegment || urlSegment.includes(':')) return

    let breadcrumbLabel
    let breadcrumbMenu
    let menu

    // loop through the whole menu structure and populate the breadcrumb label and menu

    if (index === 0) {
      menu = menuToSearch?.find((item) => item.urlSegment === urlSegment)
      breadcrumbMenu = menuItems
    } else if (menuToSearch?.children) {
      menu = menuToSearch.children.find((item) => item.urlSegment === urlSegment)
      breadcrumbMenu = menuToSearch.children
    }

    // update menuToSearch for next iteration
    menuToSearch = menu

    breadcrumbLabel = menu?.label || ''
    breadcrumbLevels[index] = { label: breadcrumbLabel, urlSegment, breadcrumbMenu }
  })

  return breadcrumbLevels
}
